import React, { useEffect, useState } from 'react';
import { CheckCircle2, Copy, Mail, Send, UserPlus, X } from 'lucide-react';
import { clientPortalAdminApi } from '../utils/api';

interface InviteClientModalProps {
  open: boolean;
  client: { id: string; name: string; email?: string | null } | null;
  onClose: () => void;
  onSent?: () => void;
  showToast?: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
}

const InviteClientModal: React.FC<InviteClientModalProps> = ({
  open,
  client,
  onClose,
  onSent,
  showToast,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<null | { setupUrl: string; expiresAt: string }>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    setSubmitting(false);
    setCopied(false);
  }, [open, client?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !client) return null;

  const missingEmail = !client.email;

  const submit = async () => {
    if (missingEmail) return;
    setSubmitting(true);
    setError(null);
    try {
      const res: any = await clientPortalAdminApi.invite(client.id);
      const payload = res?.data?.data || res?.data || res;
      const setupUrl: string = payload?.setupUrl || '';
      const expiresAt: string = payload?.expiresAt || '';
      setResult({ setupUrl, expiresAt });
      onSent?.();
      showToast?.('Portal invite sent', 'success');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not send the invite. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result?.setupUrl) return;
    try {
      await navigator.clipboard.writeText(result.setupUrl);
      setCopied(true);
      showToast?.('Setup link copied', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast?.('Could not copy to clipboard', 'error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E6E4]">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-orange-500 text-white flex items-center justify-center">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#181818]">
                {result ? 'Portal invite sent' : 'Invite to client portal'}
              </h2>
              <p className="text-xs text-[#686E6C] mt-0.5">
                {result
                  ? 'They’ll receive an email with a setup link.'
                  : `Send ${client.name || 'this client'} a secure link to set up their portal.`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-[#F5F4F2] text-[#686E6C]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!result ? (
          <div className="px-6 py-5 space-y-5">
            <div className="rounded-xl border border-[#E6E6E4] bg-[#F8F8F7] p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#686E6C] mb-2">
                Recipient
              </div>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white flex items-center justify-center font-semibold">
                  {String(client.name || client.email || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#181818] truncate">{client.name}</div>
                  {client.email ? (
                    <div className="text-xs text-[#686E6C] flex items-center gap-1.5 truncate">
                      <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                      {client.email}
                    </div>
                  ) : (
                    <div className="text-xs text-red-600 mt-0.5">
                      No email on file — add one before inviting.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#686E6C] mb-2">
                What they’ll get
              </div>
              <ul className="text-sm text-[#181818] space-y-1.5">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-orange-500 flex-shrink-0" />
                  A branded welcome email from your company
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-orange-500 flex-shrink-0" />
                  A secure link to set up a password
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-orange-500 flex-shrink-0" />
                  Access to their quotes, jobs, certificates &amp; invoices
                </li>
              </ul>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-[#181818] hover:bg-[#F5F4F2]"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                onClick={submit}
                disabled={submitting || missingEmail}
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Sending…' : 'Send portal invite'}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            <div className="rounded-xl border border-[#E6E6E4] bg-gradient-to-br from-orange-50/80 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#181818]">
                    Invite sent to {client.email}
                  </div>
                  <div className="text-xs text-[#686E6C] mt-1">
                    Link expires{' '}
                    {result.expiresAt
                      ? new Date(result.expiresAt).toLocaleDateString('en-GB')
                      : 'in 7 days'}
                    .
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#181818] mb-1.5">
                Setup link (share directly if email doesn’t arrive)
              </label>
              <div className="flex items-stretch gap-2">
                <input
                  readOnly
                  value={result.setupUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 h-11 px-3 rounded-lg border border-[#E6E6E4] bg-[#F8F8F7] text-xs font-mono text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 px-3 rounded-lg border border-[#E6E6E4] bg-white text-sm font-medium text-[#181818] hover:bg-[#F5F4F2]"
                >
                  <Copy className="h-4 w-4" />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end pt-1">
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 shadow-sm"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteClientModal;
